if (!customElements.get('fbt-bundle')) {
  customElements.define(
    'fbt-bundle',
    class FbtBundle extends HTMLElement {
      connectedCallback() {
        this.totalEl = this.querySelector('.fbt__total-price');
        this.submitButton = this.querySelector('.fbt__submit');
        this.messageEl = this.querySelector('.fbt__message');
        this.cartAddUrl = this.dataset.cartAddUrl;
        this.cartUrl = this.dataset.cartUrl;

        // Delegated: recommendation items are fetched in after this runs, so
        // per-checkbox listeners bound here would miss them entirely.
        this.addEventListener('change', (event) => {
          if (event.target.classList.contains('fbt__checkbox')) this.updateTotal();
        });
        this.addEventListener('fbt:recommendations-loaded', () => this.updateTotal());

        this.submitButton?.addEventListener('click', this.addBundleToCart.bind(this));

        this.updateTotal();
      }

      // Always re-read the DOM — the item set changes when recommendations land.
      get checkboxes() {
        return Array.from(this.querySelectorAll('.fbt__checkbox'));
      }

      updateTotal() {
        let total = 0;

        this.checkboxes.forEach((checkbox) => {
          if (!checkbox.checked) return;
          const item = checkbox.closest('.fbt__item');
          const priceEl = item?.querySelector('.fbt__item-price');
          const price = parseInt(priceEl?.dataset.price || '0', 10);
          total += price;
        });

        if (this.totalEl) {
          this.totalEl.textContent = this.formatMoney(total);
        }

        const anySelected = this.checkboxes.some((checkbox) => checkbox.checked);
        if (this.submitButton) this.submitButton.disabled = !anySelected;
      }

      formatMoney(cents) {
        if (window.Shopify && typeof window.Shopify.formatMoney === 'function' && window.theme?.moneyFormat) {
          return window.Shopify.formatMoney(cents, window.theme.moneyFormat);
        }

        // Otherwise honour the shop's own money_format from Liquid. The old
        // fallback hardcoded a rupee symbol, which rendered the wrong currency
        // on every store that isn't INR.
        const format = this.dataset.moneyFormat;
        if (format) {
          const amount = cents / 100;
          const withDecimals = amount.toFixed(2);
          const group = (value, sep) => value.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
          const [whole, frac] = withDecimals.split('.');
          const replacements = {
            amount: `${group(whole, ',')}.${frac}`,
            amount_no_decimals: group(String(Math.round(amount)), ','),
            amount_with_comma_separator: `${group(whole, '.')},${frac}`,
            amount_no_decimals_with_comma_separator: group(String(Math.round(amount)), '.'),
            amount_with_space_separator: `${group(whole, ' ')},${frac}`,
            amount_no_decimals_with_space_separator: group(String(Math.round(amount)), ' '),
            amount_with_apostrophe_separator: `${group(whole, "'")}.${frac}`,
          };
          const rendered = format.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, token) =>
            token in replacements ? replacements[token] : match
          );
          if (rendered !== format) return rendered;
        }

        return (cents / 100).toFixed(2);
      }

      async addBundleToCart() {
        const items = this.checkboxes
          .filter((checkbox) => checkbox.checked)
          .map((checkbox) => {
            const item = checkbox.closest('.fbt__item');
            const variantId = item.querySelector('.fbt__variant-id')?.value;
            return { id: variantId, quantity: 1 };
          })
          .filter((item) => item.id);

        if (items.length === 0) return;

        this.classList.add('is-loading');
        this.hideMessage();

        try {
          const response = await fetch(this.cartAddUrl + '.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items }),
          });

          const data = await response.json();

          if (!response.ok) {
            throw new Error(data?.description || 'Could not add items to cart');
          }

          this.showMessage(`${items.length} item(s) added to cart.`, 'success');

          document.dispatchEvent(new CustomEvent('fbt:added', { detail: { items: data } }));

          // Refresh the header cart count if Dawn's cart icon bubble is present.
          const cartCountBubble = document.querySelector('.cart-count-bubble');
          const cartResponse = await fetch('/cart.js');
          const cart = await cartResponse.json();

          if (cartCountBubble) {
            const countSpan = cartCountBubble.querySelector('span[aria-hidden="true"]');
            if (countSpan) countSpan.textContent = cart.item_count;
          } else {
            const cartIcon = document.querySelector('#cart-icon-bubble');
            if (cartIcon) window.location.href = this.cartUrl;
          }
        } catch (error) {
          this.showMessage(error.message || 'Something went wrong. Please try again.', 'error');
        } finally {
          this.classList.remove('is-loading');
        }
      }

      showMessage(text, type) {
        if (!this.messageEl) return;
        this.messageEl.textContent = text;
        this.messageEl.hidden = false;
        this.messageEl.className = `fbt__message fbt__message--${type}`;
      }

      hideMessage() {
        if (!this.messageEl) return;
        this.messageEl.hidden = true;
      }
    }
  );
}

/* Fetches the bundle's recommendation items from Shopify's Product
   Recommendations API. Modelled on Dawn's <product-recommendations> (lazy via
   IntersectionObserver, section re-render rather than JSON so prices, images
   and translations stay server-rendered), but purpose-built because this
   bundle needs two things Dawn's generic element does not provide: it must
   re-notify <fbt-bundle> after the swap, and it must hide the whole section
   when the API returns nothing — a "bundle" of one product is meaningless. */
if (!customElements.get('fbt-recommendations')) {
  customElements.define(
    'fbt-recommendations',
    class FbtRecommendations extends HTMLElement {
      connectedCallback() {
        // Already populated (this render came from the API itself) — nothing to do.
        if (this.children.length > 0) return;

        const productId = this.dataset.productId;
        if (!productId || !this.dataset.url) return this.hideSection();

        // This element is display:contents so it generates no layout box of its
        // own — an IntersectionObserver pointed at `this` would never fire.
        // Observe the nearest ancestor that actually has a box.
        const target = this.closest('fbt-bundle') || this.parentElement || this;

        this.observer = new IntersectionObserver(
          (entries, observer) => {
            if (!entries[0].isIntersecting) return;
            observer.unobserve(target);
            this.load(productId);
          },
          { rootMargin: '0px 0px 400px 0px' }
        );
        this.observer.observe(target);
      }

      disconnectedCallback() {
        this.observer?.disconnect();
      }

      async load(productId) {
        const url = `${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`;
        try {
          const response = await fetch(url);
          if (!response.ok) throw new Error(`Recommendations request failed: ${response.status}`);

          const holder = document.createElement('div');
          holder.innerHTML = await response.text();
          const fetched = holder.querySelector('fbt-recommendations');

          if (!fetched || !fetched.querySelector('.fbt__item')) return this.hideSection();

          this.innerHTML = fetched.innerHTML;
          this.closest('fbt-bundle')?.dispatchEvent(
            new CustomEvent('fbt:recommendations-loaded', { bubbles: false })
          );
        } catch (error) {
          console.error(error);
          this.hideSection();
        }
      }

      // No recommendations: remove the section rather than leave a one-item bundle.
      hideSection() {
        const section = this.closest('.shopify-section') || this.closest('.fbt');
        if (section) section.hidden = true;
      }
    }
  );
}
